import { DownArrowIcon } from "@xola/icons";
import { Button, getSymbol, InlineValuePopover, Input, Label, Select } from "@xola/ui-kit";
import clsx from "clsx";
import { FormField } from "common";
import { useField, useFormikContext } from "formik";
import React, { ChangeEvent, useState } from "react";
import { CouponAlgorithm, CouponScope, LineItemTemplateCode, SelectionState } from "types";
import { NumberInput } from "@/components/Common/NumberInput";
import { ListFilter, SelectionWithName } from "@/components/Dashboard/Filters/ListFilter";
import type { MembershipBenefitsFormData } from "@/components/Product/Membership/MembershipBenefits/MembershipBenefits";
import { PopoverListChoice } from "@/components/Product/Questionnaire/PopoverListChoice";
import { useSeller } from "@/hooks/useAccount";
import { useLocalizationStore } from "@/stores/localizationStore";

export enum DemographicLimitType {
    LIMITED = "limited",
    UNLIMITED = "unlimited",
}

export const MembershipBenefitsDiscountForm = () => {
    const { values, errors, touched } = useFormikContext<MembershipBenefitsFormData>();

    return (
        <div className="mb-4">
            {touched.amount && errors.amount && <Label className="!text-danger">Amount {errors.amount}</Label>}

            <FormField name="benefits" className="mb-3 flex space-x-2">
                <AmountInlineValuePopover />
                <span>off</span>
                <ScopeInlineValuePopover />
                {values.scope === CouponScope.PER_PERSON && (
                    <>
                        <span>for</span>
                        <DemographicsInlinePopover />
                    </>
                )}
            </FormField>

            {values.scope === CouponScope.PER_PERSON && (
                <div className="flex space-x-2">
                    <DemographicsLimitInlineValuePopover />
                    <span>guest(s) eligible for discount</span>
                </div>
            )}
        </div>
    );
};

const AmountInlineValuePopover = () => {
    const seller = useSeller();
    const locale = useLocalizationStore().getLocale();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext<MembershipBenefitsFormData>();
    const [amount, setAmount] = useState<number | null>(values.amount ?? null);
    const [algorithm, setAlgorithm] = useState<CouponAlgorithm>(values.algorithm);
    const amountError = touched.amount && errors.amount;

    const handleApply = () => {
        setFieldValue("amount", amount ?? 0);
        setFieldValue("algorithm", algorithm);
        setTimeout(() => setFieldTouched("amount", true));
        setIsOpen(false);
    };

    const text =
        values.algorithm === CouponAlgorithm.ABSOLUTE
            ? `${getSymbol(seller.currency, locale)} ${values.amount}`
            : `${values.amount}%`;

    return (
        <InlineValuePopover
            showArrow
            text={text}
            isOpen={isOpen}
            error={amountError}
            data-testid="membership-discount-value"
            classNames={{
                textField: clsx({ "!text-danger !border-b-danger": !!amountError }, "membership-discount-value"),
            }}
            onClick={() => setIsOpen(true)}
            onClickOutside={() => setIsOpen(false)}
        >
            <div className="flex space-x-2">
                <div className="inline-flex">
                    <div>
                        <NumberInput
                            allowDecimal
                            placeholder="0"
                            className="h-10 rounded-r-none border-r-0"
                            data-testid="membership-discount-value-input"
                            value={amount}
                            onChange={(value) => setAmount(value ? Number(value) : null)}
                        />
                    </div>
                    <div className="w-30">
                        <Select
                            className="h-10 rounded-l-none"
                            data-testid="membership-discount-algorithm-input"
                            value={algorithm}
                            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                                setAlgorithm(event.target.value as CouponAlgorithm);
                            }}
                        >
                            <option value={CouponAlgorithm.ABSOLUTE} className="no-translate">
                                {getSymbol(seller.currency, locale)}
                            </option>
                            <option value={CouponAlgorithm.PERCENT}>%</option>
                        </Select>
                    </div>
                </div>

                <Button onClick={handleApply}>Apply</Button>
            </div>
        </InlineValuePopover>
    );
};

const ScopeInlineValuePopover = () => {
    const [{ value }, , { setValue }] = useField<CouponScope>("scope");

    return (
        <PopoverListChoice<CouponScope>
            dataTestId="membership-discount-scope"
            value={value}
            values={[
                {
                    value: CouponScope.PER_BOOKING,
                    label: "per purchase",
                },
                {
                    value: CouponScope.PER_PERSON,
                    label: "per person",
                },
            ]}
            onChange={setValue}
        />
    );
};

const DemographicsInlinePopover = () => {
    const [{ value }, , { setValue }] = useField<SelectionState>("demographics");

    return (
        <ListFilter
            label="Demographic"
            collection={["lineItemTemplates"]}
            value={value}
            defaultParams={{ type: "demographic", "code[notEqual]": LineItemTemplateCode.PER_OUTING_PRICE }}
            target={DemographicText}
            show={{ search: false }}
            onChange={(demographics) => {
                setValue(demographics);
            }}
        />
    );
};

interface DemographicTextProps {
    value: SelectionWithName;
    onClick: () => void;
}

const DemographicText = ({ value, onClick }: DemographicTextProps) => {
    const count = value.selected.length;

    return (
        <div
            className="inline-flex cursor-pointer items-center"
            data-testid="membership-discount-demographics-selection"
            onClick={onClick}
        >
            <span className="mr-1 border-b border-b-black font-bold text-black">
                {count === 0 || value.all ? (
                    "All Demographics "
                ) : (
                    <>
                        <var data-var="demographicsCount">{count}</var> demographics
                    </>
                )}
            </span>
            <DownArrowIcon size="medium" className="mt-1" />
        </div>
    );
};

const DemographicsLimitInlineValuePopover = () => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { values, setFieldValue } = useFormikContext<MembershipBenefitsFormData>();
    const [limit, setLimit] = useState<number>(values.demographicsLimit);
    const [type, setType] = useState<DemographicLimitType>(DemographicLimitType.LIMITED);

    const handleApply = () => {
        setFieldValue("demographicsLimit", limit);
        setIsOpen(false);
    };

    const handleLimitTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value as DemographicLimitType;
        setType(value);
        setLimit(value === DemographicLimitType.UNLIMITED ? 0 : 1);
    };

    return (
        <InlineValuePopover
            showArrow
            isOpen={isOpen}
            text={values.demographicsLimit || "Unlimited"}
            classNames={{ textField: "membership-discount-limit-text" }}
            onClick={() => setIsOpen(true)}
            onClickOutside={() => setIsOpen(false)}
        >
            <div className="flex space-x-2">
                <div className="inline-flex">
                    <div className="flex-1">
                        <Input
                            disabled={type === DemographicLimitType.UNLIMITED}
                            type="number"
                            value={limit}
                            min={0}
                            data-testid="membership-discount-limit-value"
                            placeholder="0"
                            className="h-10 rounded-r-none border-r-0"
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setLimit(Math.round(Number(e.target.value ?? 0)))
                            }
                        />
                    </div>
                    <div className="flex-1">
                        <Select
                            className="h-10 rounded-l-none"
                            value={type}
                            data-testid="membership-discount-limit-type"
                            onChange={handleLimitTypeChange}
                        >
                            <option value={DemographicLimitType.LIMITED}>Limited</option>
                            <option value={DemographicLimitType.UNLIMITED}>Unlimited</option>
                        </Select>
                    </div>
                </div>

                <Button onClick={handleApply}>Apply</Button>
            </div>
        </InlineValuePopover>
    );
};
